import InfiniteScroll from 'react-infinite-scroll-component';
import { BirthdayGroup, RangeKey } from './BirthdayService';
import DateRail from './DateRail';
import BirthdayAlumniCard from './BirthdayAlumniCard';

interface Props {
    groups: BirthdayGroup[];
    today: Date;
    rangeKey: RangeKey;
    totalForRange: number;
    isLoading: boolean;
    isAppending: boolean;
    hasMore: boolean;
    appendError: boolean;
    initialError: boolean;
    tabCounts: Record<RangeKey, number>;
    hasActiveFilters: boolean;
    onLoadMore: () => void;
    onClearFilters: () => void;
}

const RANGE_PHRASE: Record<RangeKey, string> = {
    7: 'next 7 days',
    30: 'next 30 days',
    90: 'next 90 days',
    365: 'this year',
};

function countCards(groups: BirthdayGroup[]): number {
    return groups.reduce((sum, g) => sum + g.alumni.length, 0);
}

const SkeletonBlock = () => (
    <div className="bd-skeleton">
        {[0, 1, 2].map((i) => (
            <div className="bd-skeleton-row" key={i}>
                <div className="bd-skeleton-rail" />
                <div className="bd-skeleton-grid">
                    {[0, 1, 2].map((j) => (
                        <div className="bd-skeleton-card" key={j} />
                    ))}
                </div>
            </div>
        ))}
    </div>
);

const BirthdayList = ({
    groups,
    today,
    rangeKey,
    totalForRange,
    isLoading,
    isAppending,
    hasMore,
    appendError,
    initialError,
    tabCounts,
    hasActiveFilters,
    onLoadMore,
    onClearFilters,
}: Props) => {
    if (initialError) {
        return (
            <div className="bd-error-block text-center py-5">
                <p className="font-16 mb-3">Couldn't load birthdays.</p>
                <button type="button" className="btn btn-green" onClick={() => window.location.reload()}>
                    Retry
                </button>
            </div>
        );
    }

    if (isLoading && groups.length === 0) {
        return <SkeletonBlock />;
    }

    // Population-zero: institute genuinely has no alumni with DOB on file (across the whole year).
    const populationZero = tabCounts[365] === 0 && !hasActiveFilters;
    if (populationZero) {
        return (
            <div className="bd-empty-block text-center py-5">
                <h3 className="font-20 font-600 mb-2">Birthday data isn't available yet.</h3>
                <p className="font-14 text-gray-53 mb-0">
                    Once alumni complete their profiles with a date of birth, they'll start showing up here.
                </p>
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="bd-empty-block text-center py-5">
                <p className="font-16 mb-1">No alumni have a birthday in the selected range.</p>
                <p className="font-14 text-gray-53 mb-3">Try a wider time range or clear your filters.</p>
                {hasActiveFilters && (
                    <button type="button" className="btn btn-green" onClick={onClearFilters}>
                        Clear filters
                    </button>
                )}
            </div>
        );
    }

    const sectionHeadingText = `${totalForRange} ${totalForRange === 1 ? 'birthday' : 'birthdays'} in ${RANGE_PHRASE[rangeKey]}`;
    const cardCount = countCards(groups);

    return (
        <div className="bd-list">
            <h3 className="bd-section-heading font-18 font-600 mb-3">{sectionHeadingText}</h3>

            <InfiniteScroll
                dataLength={cardCount}
                next={onLoadMore}
                hasMore={hasMore && !appendError}
                loader={null}
                scrollThreshold={0.85}>
                {groups.map((group, idx) => (
                    <div className="bd-group" key={`${group.birthdayThisYear}-${idx}`}>
                        <DateRail
                            birthdayThisYear={group.birthdayThisYear}
                            daysUntil={group.daysUntil}
                            today={today}
                        />
                        <div className="bd-group-grid row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                            {group.alumni.map((a) => (
                                <BirthdayAlumniCard key={a.studentUuid} alumni={a} />
                            ))}
                        </div>
                    </div>
                ))}
            </InfiniteScroll>

            {isAppending && (
                <div className="text-center py-3 bd-append-spinner">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    <span className="ms-2 font-14">Loading more…</span>
                </div>
            )}

            {appendError && (
                <div className="text-center py-3">
                    <p className="font-14 mb-2">Couldn't load more.</p>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onLoadMore}>
                        Retry
                    </button>
                </div>
            )}

            {!hasMore && !isAppending && !appendError && (
                <div className="text-center py-3 font-13 text-gray-53">That's all for this range.</div>
            )}
        </div>
    );
};

export default BirthdayList;
